/**
 * useFavorites — hook partagé pour les favoris d'un utilisateur connecté.
 *
 * Stratégie :
 *  - Cache singleton en mémoire (Set d'IDs) chargé une seule fois au login
 *  - Tous les `PropertyCard` lisent le même Set → pas de N appels API
 *  - `add` / `remove` mettent à jour le cache localement puis appellent l'API ;
 *    en cas d'échec, on rollback l'état local
 *  - Pour un utilisateur déconnecté : `loggedIn=false`, les méthodes retournent
 *    `{ ok:false, reason:'auth_required' }` pour que le composant guide l'utilisateur
 *    vers la connexion plutôt que de faire croire à un succès fantôme.
 */
import { useEffect, useState, useCallback } from 'react';
import { favoriteAPI } from '../utils/api';

let _cache = null;       // Set<string> | null — null avant le 1er fetch
let _loading = false;
let _loaded = false;
const _subscribers = new Set();

const notify = () => { _subscribers.forEach((fn) => fn()); };

const getUser = () => {
  try {
    return JSON.parse(localStorage.getItem('user') || 'null');
  } catch {
    return null;
  }
};

const loadOnce = async () => {
  if (_loaded || _loading) return;
  const user = getUser();
  if (!user?.id && !user?._id) {
    // Logged out — leave cache null (no fake state)
    _loaded = true;
    notify();
    return;
  }
  _loading = true;
  try {
    const res = await favoriteAPI.getAll();
    const list = Array.isArray(res?.data) ? res.data : (res?.data?.favorites || []);
    _cache = new Set(list.map((f) => String(f.property_id || f.propertyId || f._id || f.id)));
  } catch {
    _cache = new Set();
  } finally {
    _loading = false;
    _loaded = true;
    notify();
  }
};

export const useFavorites = () => {
  // Force re-render when the shared cache mutates
  const [, setTick] = useState(0);
  useEffect(() => {
    const fn = () => setTick((t) => t + 1);
    _subscribers.add(fn);
    if (!_loaded) loadOnce();
    return () => { _subscribers.delete(fn); };
  }, []);

  const loggedIn = !!(getUser()?.id || getUser()?._id);

  const has = useCallback((id) => {
    if (!id || !_cache) return false;
    return _cache.has(String(id));
  }, []);

  const add = useCallback(async (id) => {
    if (!loggedIn) return { ok: false, reason: 'auth_required' };
    const key = String(id);
    if (!_cache) _cache = new Set();
    _cache.add(key);
    notify();
    try {
      await favoriteAPI.add(id);
      return { ok: true };
    } catch (err) {
      _cache.delete(key);
      notify();
      return { ok: false, reason: err?.response?.data?.detail || 'network' };
    }
  }, [loggedIn]);

  const remove = useCallback(async (id) => {
    if (!loggedIn) return { ok: false, reason: 'auth_required' };
    const key = String(id);
    if (_cache) _cache.delete(key);
    notify();
    try {
      await favoriteAPI.remove(id);
      return { ok: true };
    } catch (err) {
      if (_cache) _cache.add(key);
      notify();
      return { ok: false, reason: err?.response?.data?.detail || 'network' };
    }
  }, [loggedIn]);

  return { has, add, remove, loggedIn, loaded: _loaded };
};

// Reset on logout — caller (e.g. logout button) can invoke this to wipe cache.
export const resetFavoritesCache = () => {
  _cache = null;
  _loaded = false;
  _loading = false;
  notify();
};

export default useFavorites;
